import { useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { toast } from "react-hot-toast";
import { useAuth } from "../../lib/useAuth";
import { pathAfterAuth, pathAfterGuestDemo } from "../../lib/after-auth";
import { adoptAuthToken } from "../../lib/adopt-token";
import {
  GUEST_SHARE_DENIED,
  guestShareWasBlocked,
} from "../../lib/guest-share";
import { getBaseUrl } from "../../lib/base-url";
import { isDismissedPopup, signInWithGoogle } from "../../lib/firebase";
import { prepareOAuthNonce } from "../../lib/oauth-handoff";
import type { RegisterDto, LoginDto } from "../../lib/generated/api-client";
import { Input } from "../../components/ui/Input";
import { PasswordInput } from "../../components/ui/PasswordInput";

export default function Auth() {
  const {
    login,
    register,
    loginAsGuest,
    error: authError,
    isLoading,
    guest,
    user,
    logout,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const inviteToken = searchParams.get("invite")?.trim() ?? "";
  // MOSA-364: the connect wizard sends ?next=<app path>; come back to it after
  // sign-up, sign-in or guest access. Only an in-app path is accepted.
  const nextParam = searchParams.get("next")?.trim() ?? "";
  const nextPath =
    nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "";
  // MOSA-438: the website "Join the pilot" link lands on ?pilot=1. Keep the
  // value in the URL for later API or analytics reads; open in sign-up mode.
  const pilotParam = searchParams.get("pilot");
  const acceptingInvite = inviteToken.length > 0;
  const [isLogin, setIsLogin] = useState(() => pilotParam !== "1");
  const [guestName] = useState("");
  const [guestId, setGuestId] = useState<string | undefined>("");
  const [showGuestRestore, setShowGuestRestore] = useState(false);
  const [oauth, setOauth] = useState<{
    google: boolean;
    github: boolean;
    firebase: boolean;
  }>({
    google: false,
    github: false,
    firebase: false,
  });
  // MOSA-694 item 1: the provider probe answers after mount. The card is
  // centred in the viewport, so an OAuth button that arrives late moves the
  // whole card up. Hold a button-sized row until the probe answers.
  const [providersResolved, setProvidersResolved] = useState(false);
  const [firebaseLoading, setFirebaseLoading] = useState(false);
  // MOSA-545: loginAsGuest has already set `guest` while pathAfterGuestDemo
  // still fetches the landing path. Without this the card re-renders as a bare
  // Sign in form for that wait.
  const [openingDemo, setOpeningDemo] = useState(false);
  const [formData, setFormData] = useState<RegisterDto & LoginDto>({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
  });

  useEffect(() => {
    const storedGuest = localStorage.getItem("guest");
    if (storedGuest) {
      setGuestId(storedGuest);
      setShowGuestRestore(true);
    }
    fetch(`${getBaseUrl()}/auth/providers`)
      .then((r) =>
        r.ok ? r.json() : { google: false, github: false, firebase: false },
      )
      .then((p) =>
        setOauth({
          google: Boolean(p.google),
          github: Boolean(p.github),
          firebase: Boolean(p.firebase),
        }),
      )
      .catch(() => undefined)
      .finally(() => setProvidersResolved(true));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("oauth") === "failed") {
      toast.error("OAuth sign-in failed");
    }
  }, [location.search]);

  const hasOauthProvider = oauth.google || oauth.github || oauth.firebase;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isLogin) {
        const loginDto: LoginDto = {
          email: formData.email,
          password: formData.password,
        };
        await login(loginDto);
        if (guestShareWasBlocked()) {
          toast.error(GUEST_SHARE_DENIED);
          navigate("/data-sources");
          return;
        }
        toast.success("Successfully logged in!");
        if (acceptingInvite) {
          navigate("/invite/" + encodeURIComponent(inviteToken));
        } else if (nextPath) {
          navigate(nextPath);
        } else {
          const token = useAuth.getState().token;
          const next = token ? await pathAfterAuth(token) : "/data-sources";
          navigate(next);
        }
      } else {
        const registerDto: RegisterDto = {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
        };
        await register(registerDto);
        toast.success("Successfully registered!");
        navigate({
          pathname: "/auth/verify-email",
          search:
            "?email=" +
            encodeURIComponent(formData.email) +
            (acceptingInvite
              ? "&invite=" + encodeURIComponent(inviteToken)
              : "") +
            (nextPath ? "&next=" + encodeURIComponent(nextPath) : "") +
            (pilotParam != null && pilotParam !== ""
              ? "&pilot=" + encodeURIComponent(pilotParam)
              : ""),
        });
      }
    } catch (error: any) {
      console.error("Auth error:", error);
      toast.error("Authentication failed");
    }
  };

  /**
   * Server-side Google OAuth (`GET /auth/google`). Bind the redirect to this
   * tab with a nonce hash before leaving, same as the Sheets wizard start.
   */
  const handleServerGoogleSignIn = async (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    event.preventDefault();
    try {
      const { nonceHash } = await prepareOAuthNonce();
      const url = new URL(`${getBaseUrl()}/auth/google`);
      url.searchParams.set("nonceHash", nonceHash);
      if (acceptingInvite) {
        url.searchParams.set("invite", inviteToken);
      }
      window.location.assign(url.toString());
    } catch {
      toast.error("Could not start Google sign-in");
    }
  };

  const handleFirebaseSignIn = async () => {
    setFirebaseLoading(true);
    try {
      const idToken = await signInWithGoogle();
      // Not the generated client: POST /auth/firebase postdates the checked-in
      // api-client.ts, and regenerating it needs a live API. Same raw fetch the
      // providers lookup above already uses.
      const res = await fetch(`${getBaseUrl()}/auth/firebase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      if (!res.ok) {
        throw new Error(`Firebase sign-in rejected (${res.status})`);
      }
      const { token } = (await res.json()) as { token: string };
      const next = await adoptAuthToken(token);
      if (guestShareWasBlocked()) {
        toast.error(GUEST_SHARE_DENIED);
        navigate("/data-sources");
        return;
      }
      if (acceptingInvite) {
        navigate("/invite/" + encodeURIComponent(inviteToken));
      } else if (nextPath) {
        // MOSA-364: "Continue with Google" must come back to the wizard too,
        // the same as the email paths above.
        navigate(nextPath);
      } else {
        navigate(next);
      }
    } catch (error: any) {
      // Closing the popup is a decision, not a failure. Stay quiet.
      if (isDismissedPopup(error)) {
        return;
      }
      console.error("Firebase sign-in error:", error);
      toast.error("OAuth sign-in failed");
    } finally {
      setFirebaseLoading(false);
    }
  };

  const handleGuestAccess = async () => {
    setOpeningDemo(true);
    try {
      await loginAsGuest(guestName, guestId);
      toast.success("Opening the demo as a guest");
      if (nextPath) {
        navigate(nextPath);
        return;
      }
      const token = useAuth.getState().token;
      const next = token ? await pathAfterGuestDemo(token) : "/data-sources";
      navigate(next);
    } catch (error: any) {
      console.error("Guest access error:", error);
      toast.error("Guest access failed");
      setOpeningDemo(false);
    }
  };

  if (openingDemo) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-3 bg-od-bg px-4" role="status" aria-live="polite">
        <div className="h-6 w-6 shrink-0 rounded-full border-2 border-od-border border-t-od-primary motion-safe:animate-spin" aria-hidden="true" />
        <p className="text-sm font-medium text-od-text">
          Loading your data...
        </p>
      </div>
    );
  }

  if (user) {
    return (
      <div className="min-h-screen bg-od-bg flex flex-col items-center justify-center px-4 py-8">
        <div className="max-w-lg w-full space-y-4 bg-od-surface p-8 rounded-2xl shadow-lg border border-od-border">
          <div className="text-center space-y-3">
            <h2 className="text-xl font-bold text-od-text">Welcome back!</h2>
            <p className="text-od-muted text-sm">Signed in as {user.email}</p>
            {user.firstName && (
              <p className="text-od-muted text-sm">
                {user.firstName} {user.lastName}
              </p>
            )}
            <Button
              onClick={() => {
                logout();
                navigate("/auth");
              }}
              variant="primary" className="w-full text-base"
            >
              Sign out
            </Button>

            <Link
              to="/"
              className="mt-3 flex min-h-11 min-w-11 items-center justify-center rounded-sm text-od-primary-text hover:underline font-semibold text-sm transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-surface sm:min-h-0 sm:min-w-0 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
            >
              Back to your dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Always a full-page auth surface. Guests who still hold a JWT used to see
  // a small "Connect your account" card beside the demo rail; that looked like
  // a stray overlay on the dashboard. Sign out first if you need a clean door.
  return (
    <div className="min-h-screen bg-od-bg flex flex-col items-center justify-center px-4 py-8">
      <div className="mb-8 text-center">
        <a
          href="https://onlydash.io"
          className="inline-flex min-h-11 min-w-11 items-center sm:min-h-0 sm:min-w-0 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 text-3xl font-bold tracking-tight text-od-text rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-bg"
        >
          onlydash<span className="text-od-primary">.</span>
        </a>
        <h1 className="mt-3 text-2xl font-semibold text-od-text">
          {guest && !isLogin
            ? "Keep your guest work"
            : isLogin
              ? "Sign in"
              : "Sign up"}
        </h1>
        <p className="mt-2 text-od-muted text-base">
          {guest && !isLogin
            ? "Create an account to keep the work from your guest session."
            : acceptingInvite
              ? isLogin
                ? "You are accepting an invite. Sign in with the email address that got the invite."
                : "You are accepting an invite. Sign up with the email address that got the invite."
              : isLogin
                ? "Use your email address or your Google account."
                : "Make your account with your email address or your Google account."}
        </p>
      </div>

      <div className="max-w-lg w-full space-y-6 bg-od-surface p-8 rounded-2xl shadow-lg border border-od-border">
        {authError && (
          <div
            role="alert"
            className="bg-od-surface border border-od-danger text-od-danger px-3 py-2 rounded-lg text-sm"
          >
            {authError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">

          <div className="space-y-3">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="firstName"
                    className="text-xs font-semibold text-od-text block mb-1.5"
                  >
                    First name
                  </label>
                  <Input
                    id="firstName"
                    type="text"
                    autoComplete="given-name"
                    required
                    disabled={isLoading}
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor="lastName"
                    className="text-xs font-semibold text-od-text block mb-1.5"
                  >
                    Last name
                  </label>
                  <Input
                    id="lastName"
                    type="text"
                    autoComplete="family-name"
                    required
                    disabled={isLoading}
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                  />
                </div>
              </div>
            )}
            <div>
              <label
                htmlFor="email"
                className="text-xs font-semibold text-od-text block mb-1.5"
              >
                Email address
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                disabled={isLoading}
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="name@company.com"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="text-xs font-semibold text-od-text block mb-1.5"
              >
                Password
              </label>
              <PasswordInput
                id="password"
                required
                disabled={isLoading}
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                autoComplete={isLogin ? "current-password" : "new-password"}
              />
            </div>
          </div>

          <div>
            <Button
              type="submit"
              disabled={isLoading}
              variant="primary" className="w-full text-base"
            >
              {isLoading
                ? "Loading…"
                : guest && !isLogin
                ? "Create account"
                : isLogin
                ? "Sign in"
                : "Sign up"}
            </Button>
          </div>
        </form>

        {!guest && (!providersResolved || hasOauthProvider) && (
          <div className="space-y-3">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-od-border"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-4 bg-od-surface text-od-muted font-medium">
                  Or continue with
                </span>
              </div>
            </div>
            {!providersResolved ? (
              // The reserved row. `min-h-11` is the button height from
              // BUTTON_SIZES, so the real button drops into the same space.
              <div className="grid gap-2" aria-hidden="true">
                <div className="min-h-11" />
              </div>
            ) : (
              <div className="grid gap-2">
                {/* Firebase and the legacy server-side Google flow both end
                    as "Continue with Google", so only one of them renders. */}
                {oauth.firebase ? (
                  <Button
                    onClick={handleFirebaseSignIn}
                    disabled={firebaseLoading}
                    variant="secondary" className="w-full text-sm font-semibold hover:border-od-primary"
                  >
                    {firebaseLoading ? "Loading…" : "Continue with Google"}
                  </Button>
                ) : (
                  oauth.google && (
                    <a
                      href={`${getBaseUrl()}/auth/google${
                        acceptingInvite
                          ? `?invite=${encodeURIComponent(inviteToken)}`
                          : ""
                      }`}
                      onClick={handleServerGoogleSignIn}
                      className="block w-full text-center border border-od-border hover:border-od-primary text-od-text py-2.5 px-6 rounded-lg text-sm font-semibold"
                    >
                      Continue with Google
                    </a>
                  )
                )}
                {oauth.github && (
                  <a
                    href={`${getBaseUrl()}/auth/github${
                      acceptingInvite
                        ? `?invite=${encodeURIComponent(inviteToken)}`
                        : ""
                    }`}
                    className="block w-full text-center border border-od-border hover:border-od-primary text-od-text py-2.5 px-6 rounded-lg text-sm font-semibold"
                  >
                    Continue with GitHub
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        <div className="text-center space-y-3">
          <Button
            onClick={() => setIsLogin(!isLogin)}
            disabled={isLoading}
            variant="link" size="sm" className="text-sm font-semibold text-od-primary-text hover:text-od-primary-text hover:underline"
          >
            {isLogin
              ? "Don't have an account? Sign up"
              : "Already have an account? Sign in"}
          </Button>

          {/* MOSA-694 item 3: the demo used to be a full-width button beside
              the real one. It sits at the bottom as a muted text link now, so
              it stays reachable without competing with Sign in / Sign up. */}
          {!guest && !acceptingInvite && (
            <div className="space-y-2">
              {showGuestRestore && (
                <div className="text-left">
                  <label
                    htmlFor="guestId"
                    className="text-xs font-semibold text-od-text block mb-1.5"
                  >
                    Previous session ID
                  </label>
                  <Input
                    id="guestId"
                    type="text"
                    value={guestId}
                    onChange={(e) => setGuestId(e.target.value)}
                    placeholder="Previous session ID"
                  />
                </div>
              )}
              <Button
                onClick={handleGuestAccess}
                disabled={isLoading}
                variant="link" size="sm" className="text-xs text-od-muted hover:text-od-text hover:underline"
              >
                {isLoading ? "Loading…" : "Open the demo as a guest"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
